import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { LanguageChangeService, LanguageChangeEvent } from '@core/language/language-change.service';
import { MarketplaceLanguageChangeService } from './marketplace-language-change.service';
import { OrganizationQuery } from '@core/organization/organization.query';

/**
 * Service that listens to global language changes and automatically reloads
 * the current page's data endpoint to refresh content with the new language.
 *
 * This service detects the current page context (marketplace, summary, basket, etc.)
 * and calls the appropriate API endpoint to reload data when the language changes.
 */
@Injectable({
  providedIn: 'root',
})
export class MarketplaceLanguageListenerService implements OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private languageChangeService: LanguageChangeService,
    private marketplaceLanguageChangeService: MarketplaceLanguageChangeService,
    private router: Router,
    private organizationQuery: OrganizationQuery,
  ) {
    this.initializeLanguageChangeListener();
  }

  private initializeLanguageChangeListener(): void {
    this.languageChangeService.languageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LanguageChangeEvent) => {
        this.handleLanguageChange(event);
      });
  }

  private handleLanguageChange(_event: LanguageChangeEvent): void {
    // Skip page reload if we're in a product editing context to prevent overwriting translations
    if (this.isProductEditingContext()) {
      return;
    }

    const currentUrl = this.router.url;
    const dataEndpoint = this.detectCurrentPageDataEndpoint(currentUrl);

    if (dataEndpoint) {
      this.marketplaceLanguageChangeService.reloadPageData(dataEndpoint).subscribe(
        (_result) => {
          // Page data successfully reloaded
        },
        (_error) => {},
      );
    }
  }

  /**
   * Get the current emittent ID from the organization context
   * @returns The emittent ID or null if not available
   */
  private getCurrentEmittentId(): string | null {
    const organization = this.organizationQuery.getActive();
    return organization?.type === 'emittent' ? organization?.id : null;
  }

  /**
   * Check if current URL indicates we're in a product editing context
   * This prevents the marketplace language listener from interfering with product translations
   */
  private isProductEditingContext(): boolean {
    const currentUrl = this.router.url;

    // Product editing routes can be either:
    // - /products/{id}/* (plural) - e.g., /products/123/opening, /products/123/marketing
    // - /product/{id}/* (singular) - e.g., /product/123/opening, /product/123/marketing
    const isProductDetailRoute =
      (currentUrl.includes('/products/') || currentUrl.includes('/product/')) && currentUrl.split('/').length >= 3;

    return isProductDetailRoute;
  }

  /**
   * Detects the appropriate API endpoint based on the current URL
   * @param currentUrl - The current router URL
   * @returns The API endpoint path or null if no pattern matches
   */
  private detectCurrentPageDataEndpoint(currentUrl: string): string | null {
    // 1. SUMMARY PAGES - Highest priority (most specific)
    if (currentUrl.includes('/summary')) {
      // Investor summary page: /investor/issuing/{emittentUid}/summary
      if (currentUrl.includes('/investor/issuing/') && currentUrl.includes('/summary')) {
        const emittentMatch = currentUrl.match(/\/investor\/issuing\/([^\/]+)\/summary/);
        if (emittentMatch) {
          const emittentUid = emittentMatch[1];
          return `common/products/${emittentUid}/summary`;
        }
      }

      // Regular issuing summary: /issuing/{emittentUid}/summary
      if (currentUrl.includes('/issuing/') && currentUrl.includes('/summary')) {
        const emittentMatch = currentUrl.match(/\/issuing\/([^\/]+)\/summary/);
        if (emittentMatch) {
          const emittentUid = emittentMatch[1];
          return `common/products/${emittentUid}/summary`;
        }
      }

      // Investor product summary: /investor/{investorUid}/products/{productUid}/summary
      if (currentUrl.includes('/investor/') && currentUrl.includes('/products/') && currentUrl.includes('/summary')) {
        const investorMatch = currentUrl.match(/\/investor\/([^\/]+)\/products\/([^\/]+)\/summary/);
        if (investorMatch) {
          const productUid = investorMatch[2];
          return `common/products/${productUid}/summary`;
        }
      }
    }

    // 2. SPECIFIC PRODUCT PAGES
    if (currentUrl.includes('/products/') && !currentUrl.includes('/summary')) {
      // Issuing product page: /issuing/{emittentUid}/products/{productUid}
      if (currentUrl.includes('/issuing/')) {
        const emittentMatch = currentUrl.match(/\/issuing\/([^\/]+)\/products\/([^\/]+)/);
        if (emittentMatch) {
          const emittentUid = emittentMatch[1];
          const productUid = emittentMatch[2];
          return `emittents/${emittentUid}/products/${productUid}`;
        }
      }

      // Investor product page: /investor/{investorUid}/products/{productUid}
      if (currentUrl.includes('/investor/')) {
        const investorMatch = currentUrl.match(/\/investor\/([^\/]+)\/products\/([^\/]+)/);
        if (investorMatch) {
          const investorUid = investorMatch[1];
          const productUid = investorMatch[2];
          return `investors/${investorUid}/products/${productUid}`;
        }
      }
    }

    // 3. PRODUCT LIST PAGES
    if (currentUrl.includes('/products') && !currentUrl.includes('/products/')) {
      // Issuing products list: /issuing/{emittentUid}/products
      if (currentUrl.includes('/issuing/')) {
        const emittentMatch = currentUrl.match(/\/issuing\/([^\/]+)\/products/);
        if (emittentMatch) {
          const emittentUid = emittentMatch[1];
          return `emittents/${emittentUid}/products`;
        }
      }

      // Investor products list: /investor/{investorUid}/products
      if (currentUrl.includes('/investor/')) {
        const investorMatch = currentUrl.match(/\/investor\/([^\/]+)\/products/);
        if (investorMatch) {
          const investorUid = investorMatch[1];
          return `investors/${investorUid}/products`;
        }
      }

      // Client products list: /investor/{investorUid}/clients/{clientUid}/products
      if (currentUrl.includes('/clients/')) {
        const clientMatch = currentUrl.match(/\/investor\/([^\/]+)\/clients\/([^\/]+)\/products/);
        if (clientMatch) {
          const investorUid = clientMatch[1];
          const clientUid = clientMatch[2];
          return `investors/${investorUid}/clients/${clientUid}/products`;
        }
      }
    }

    // 4. MARKETPLACE PAGES
    if (currentUrl.includes('/marketplace')) {
      // Client marketplace: /marketplace/investor/{investorUid}/clients/{clientUid}
      // Check this FIRST before the simpler investor pattern
      if (currentUrl.includes('/clients/')) {
        const clientMatch = currentUrl.match(/\/investor\/([^\/]+)\/clients\/([^\/]+)/);
        if (clientMatch) {
          const investorUid = clientMatch[1];
          const clientUid = clientMatch[2];
          return `investors/${investorUid}/clients/${clientUid}/products`;
        }
      }

      // Investor marketplace: /marketplace/investor/{investorUid}
      if (currentUrl.includes('/investor/')) {
        const investorMatch = currentUrl.match(/\/investor\/([^\/]+)/);
        if (investorMatch) {
          const investorUid = investorMatch[1];
          return `investors/${investorUid}/products`;
        }
      }
    }

    // 4.1. CLIENT'S OWN MARKETPLACE PAGES
    if (currentUrl.includes('/client/') && currentUrl.includes('/marketplace')) {
      // Client's own marketplace: /client/{clientUid}/marketplace
      const clientMatch = currentUrl.match(/\/client\/([^\/]+)\/marketplace/);
      if (clientMatch) {
        const clientUid = clientMatch[1];
        // Use the investor's client products endpoint for the client's own marketplace
        return `investors/c8756079-4766-48c7-9638-be843d42d360/clients/${clientUid}/products`;
      }
    }

    // 4.2. CLIENT'S OWN BASKET PAGES
    if (currentUrl.includes('/client/') && currentUrl.includes('/baskets/')) {
      // Client's own advisory document: /client/{clientUid}/baskets/{basketId}/advisory-document
      if (currentUrl.includes('/advisory-document')) {
        const clientMatch = currentUrl.match(/\/client\/([^\/]+)\/baskets\/([^\/]+)\/advisory-document/);
        if (clientMatch) {
          const clientUid = clientMatch[1];
          const basketId = clientMatch[2];
          // Use the investor's client baskets advisory document endpoint
          return `investors/c8756079-4766-48c7-9638-be843d42d360/clients/${clientUid}/baskets/${basketId}/advisory-document`;
        }
      }

      // Client's own basket: /client/{clientUid}/baskets/{basketId}
      const clientMatch = currentUrl.match(/\/client\/([^\/]+)\/baskets\//);
      if (clientMatch) {
        const clientUid = clientMatch[1];
        // Use the same investor's client products endpoint for basket data
        return `investors/c8756079-4766-48c7-9638-be843d42d360/clients/${clientUid}/products`;
      }
    }

    // 4.1. CLIENT DASHBOARD PAGES
    if (currentUrl.includes('/clients/') && currentUrl.includes('/dashboard')) {
      // Client dashboard marketplace: /clients/{clientUid}/dashboard/marketplace
      if (currentUrl.includes('/marketplace')) {
        const clientMatch = currentUrl.match(/\/clients\/([^\/]+)\/dashboard\/marketplace/);
        if (clientMatch) {
          const clientUid = clientMatch[1];
          // Use the investor's client products endpoint
          return `investors/c8756079-4766-48c7-9638-be843d42d360/clients/${clientUid}/products`;
        }
      }

      // Client dashboard baskets: /clients/{clientUid}/dashboard/baskets/{basketId}
      if (currentUrl.includes('/baskets/')) {
        // Advisory document page: /clients/{clientUid}/dashboard/baskets/{basketId}/advisory-document
        if (currentUrl.includes('/advisory-document')) {
          const clientMatch = currentUrl.match(/\/clients\/([^\/]+)\/dashboard\/baskets\/([^\/]+)\/advisory-document/);
          if (clientMatch) {
            const clientUid = clientMatch[1];
            const basketId = clientMatch[2];
            // Use the investor's client baskets advisory document endpoint
            return `investors/c8756079-4766-48c7-9638-be843d42d360/clients/${clientUid}/baskets/${basketId}/advisory-document`;
          }
        }

        // Regular basket page: /clients/{clientUid}/dashboard/baskets/{basketId}
        const clientMatch = currentUrl.match(/\/clients\/([^\/]+)\/dashboard\/baskets\//);
        if (clientMatch) {
          const clientUid = clientMatch[1];
          // Use the same investor's client products endpoint for basket data
          return `investors/c8756079-4766-48c7-9638-be843d42d360/clients/${clientUid}/products`;
        }
      }

      // Client dashboard transactions: /clients/{clientUid}/dashboard/transactions
      if (currentUrl.includes('/transactions')) {
        const clientMatch = currentUrl.match(/\/clients\/([^\/]+)\/dashboard\/transactions/);
        if (clientMatch) {
          const clientUid = clientMatch[1];
          // Use the same investor's client products endpoint for transaction data
          return `investors/c8756079-4766-48c7-9638-be843d42d360/clients/${clientUid}/products`;
        }
      }
    }

    // 5. ISSUING OVERVIEW PAGES
    if (currentUrl.includes('/issuing') && !currentUrl.includes('/products') && !currentUrl.includes('/summary')) {
      // Investor issuing overview: /investor/issuing
      if (currentUrl === '/investor/issuing' || currentUrl.includes('/investor/issuing')) {
        return `investors/c8756079-4766-48c7-9638-be843d42d360/products`;
      }

      // Regular issuing overview: /issuing/{emittentUid}
      const emittentMatch = currentUrl.match(/\/issuing\/([^\/]+)$/);
      if (emittentMatch) {
        const emittentUid = emittentMatch[1];
        return `emittents/${emittentUid}/products`;
      }

      // Emittent issuing tab: /issuing (shows published products)
      if (currentUrl === '/issuing') {
        const emittentId = this.getCurrentEmittentId();
        if (emittentId) {
          return `emittents/${emittentId}/products/published`;
        }
        // Fallback to default if no emittent context is available
        return `emittents/b0ffa9c6-f396-4051-8b8d-8a3618941823/products/published`;
      }
    }

    // 6. NEW PRODUCT PAGES
    if (currentUrl === '/product' || currentUrl.includes('/product')) {
      // This is the new product creation page
      // We need to determine which emittent context we're in
      const emittentId = this.getCurrentEmittentId();
      if (emittentId) {
        return `emittents/${emittentId}/products/newproduct`;
      }
      // Fallback to default if no emittent context is available
      return `emittents/b0ffa9c6-f396-4051-8b8d-8a3618941823/products/newproduct`;
    }

    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
